/*
 * mos-downloadable-files.js
 *
 * Paints a thumbnail of page 1 of each attached PDF into the canvas inside its
 * "Downloadable Files" card (sections/mos-specifications.liquid).
 *
 * Rules this file follows:
 *   - pdf.js is fetched from cdnjs only if the page actually has a [data-pdf-url]
 *     card, and only once one of those cards is near the viewport. These manuals
 *     run to ~10MB, so nothing is requested on a normal page view.
 *   - Ranged requests + disableAutoFetch mean only the first chunks of the PDF
 *     are pulled where the CDN honours Range.
 *   - Every failure path (blocked CDN, CORS, corrupt PDF, no canvas support) is
 *     swallowed and leaves the CSS-only PDF placeholder on screen.
 *   - The card is a plain <a href> download link, so it works with no JS at all.
 *   - No localStorage / sessionStorage.
 */
(function () {
  'use strict';

  if (window.__mosDlInit) return;
  window.__mosDlInit = true;

  var PDFJS_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
  var PDFJS_WORKER = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

  var MAX_CSS_WIDTH = 200; // px, cap on the rendered thumbnail width
  var MAX_DPR = 2;
  var RANGE_CHUNK_SIZE = 65536; // 64KB
  var SELECTOR = '.mos-dl__card[data-pdf-url]';

  var libPromise = null;

  function loadPdfJs() {
    if (libPromise) return libPromise;

    libPromise = new Promise(function (resolve, reject) {
      if (window.pdfjsLib) {
        resolve(window.pdfjsLib);
        return;
      }
      var script = document.createElement('script');
      script.src = PDFJS_SRC;
      script.async = true;
      script.crossOrigin = 'anonymous';
      script.onload = function () {
        if (window.pdfjsLib) resolve(window.pdfjsLib);
        else reject(new Error('pdf.js loaded but pdfjsLib is undefined'));
      };
      script.onerror = function () {
        reject(new Error('pdf.js could not be loaded'));
      };
      document.head.appendChild(script);
    }).then(function (lib) {
      try {
        lib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;
      } catch (e) {
        /* worker will fall back to the main thread; not fatal */
      }
      return lib;
    });

    return libPromise;
  }

  function paint(card) {
    var url = card.getAttribute('data-pdf-url');
    var canvas = card.querySelector('.mos-dl__canvas');

    if (!url || !canvas || !canvas.getContext) {
      card.setAttribute('data-mos-dl-state', 'skipped');
      return;
    }

    var doc = null;

    loadPdfJs()
      .then(function (lib) {
        return lib.getDocument({
          url: url,
          rangeChunkSize: RANGE_CHUNK_SIZE,
          disableAutoFetch: true,
          disableStream: false,
          withCredentials: false,
          isEvalSupported: false
        }).promise;
      })
      .then(function (pdf) {
        doc = pdf;
        return pdf.getPage(1);
      })
      .then(function (page) {
        var context = canvas.getContext('2d');
        if (!context) throw new Error('no 2d context');

        var dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
        var thumb = card.querySelector('.mos-dl__thumb');
        var boxWidth = (thumb && thumb.clientWidth) || MAX_CSS_WIDTH;
        var cssWidth = Math.max(48, Math.min(MAX_CSS_WIDTH, boxWidth));

        var natural = page.getViewport({ scale: 1 });
        var viewport = page.getViewport({ scale: (cssWidth / natural.width) * dpr });

        // Only the backing store is sized here. The stylesheet keeps the element
        // inside its box with max-width/max-height, so the aspect ratio is safe.
        canvas.width = Math.max(1, Math.round(viewport.width));
        canvas.height = Math.max(1, Math.round(viewport.height));

        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);

        return page.render({ canvasContext: context, viewport: viewport }).promise;
      })
      .then(function () {
        card.setAttribute('data-mos-dl-state', 'rendered');
      })
      .catch(function () {
        card.setAttribute('data-mos-dl-state', 'failed');
      })
      .then(function () {
        try {
          if (doc) {
            doc.cleanup();
            doc.destroy();
          }
        } catch (e) {
          /* nothing to do */
        }
      });
  }

  function claim(card) {
    if (card.getAttribute('data-mos-dl-state')) return false;
    card.setAttribute('data-mos-dl-state', 'pending');
    return true;
  }

  function scan() {
    var cards;
    try {
      cards = document.querySelectorAll(SELECTOR);
    } catch (e) {
      return;
    }
    if (!cards.length) return;

    if (!('IntersectionObserver' in window) || !('Promise' in window)) {
      // No lazy trigger available: still only runs on pages that have a card.
      for (var i = 0; i < cards.length; i++) {
        if (claim(cards[i])) paint(cards[i]);
      }
      return;
    }

    var observer = new IntersectionObserver(
      function (entries, obs) {
        for (var i = 0; i < entries.length; i++) {
          var entry = entries[i];
          if (!entry.isIntersecting) continue;
          obs.unobserve(entry.target);
          if (claim(entry.target)) paint(entry.target);
        }
      },
      { rootMargin: '300px 0px' }
    );

    for (var j = 0; j < cards.length; j++) {
      observer.observe(cards[j]);
    }
  }

  function boot() {
    try {
      scan();
    } catch (e) {
      /* never let a thumbnail break the page */
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  // Theme editor re-renders the section; per-card state stops duplicate work.
  document.addEventListener('shopify:section:load', boot);
})();
